import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { styled, ClassNames } from '@kufox/mui/emotion';
import useTheme from '@kufox/mui/hooks/useTheme';
import Form, { FormItem, useForm } from '@kufox/mui/Form';
import { Button, Checkbox } from '@kufox/mui';
import { NewInputEye, NewInviteCode, AssociativeEmailInput } from '../../components';
import {
  REGEXP,
  useFormValidateByLang,
  kcsensorsClick,
  kcsensorsManualTrack,
  removeSpaceSE,
} from '../../common/tools';
import { NAMESPACE, EMAIL_BIZTYPE } from '../constants';
import { useToast, useLang } from '../../hookTool';

const AgreeLabel = styled.span`
  color: ${(props) => props.theme.colors.text60};
  font-size: ${(props) => props.theme.fonts.size.lg.fontSize};
  & a {
    cursor: pointer;
    color: ${(props) => props.theme.colors.text60};
    text-decoration: underline;
    &:hover,
    &:visited,
    &:active {
      color: ${(props) => props.theme.colors.primary};
    }
  }
`;

const AgreeFormItem = styled.div`
  display: flex;
  margin-top: 16px;
`;

const AlreadyHasCount = styled.div`
  margin-top: 8px;
`;

const EmailSign = (props) => {
  const {
    agreeJSX = null,
    initEmail = '',
    bonusImg,
    onFinish,
    showDiscount,
    recallType = null,
    trackingConfig = {},
    forgetLeft,
  } = props;
  const theme = useTheme();
  const dispatch = useDispatch();
  const {
    t,
    i18n: { language },
  } = useLang();
  const toast = useToast();
  const [form] = useForm();
  const { validateFields, setFields } = form;
  const { isCount, loading } = useSelector((state) => state[NAMESPACE]);

  const handleSendCode = (params) => {
    const { email } = params || {};
    const sendPayload = {
      toast,
      email: removeSpaceSE(email),
      bizType: EMAIL_BIZTYPE,
    };
    const recallPayload = {
      language: navigator.language,
      type: recallType,
      email: removeSpaceSE(email),
    };
    dispatch({
      type: `${NAMESPACE}/sendVerifyCode`,
      payload: sendPayload,
    });
    // 注册召回接口调用
    if (recallType !== null) {
      dispatch({
        type: `${NAMESPACE}/postEmailRecall`,
        payload: recallPayload,
      });
    }
    kcsensorsClick(['emailSendCode', '1']);
  };

  const handleSubmit = async () => {
    kcsensorsClick(['newEmailConfirm', '1']);
    const values = await validateFields();
    dispatch({
      type: `${NAMESPACE}/update`,
      payload: { FirstStepData: { ...values }, registerType: 'email' },
    });
    handleSendCode(values);
  };

  const handleCheckAgree = (rule, value) => {
    if (!value) {
      return Promise.reject(t('check.required'));
    }
    return Promise.resolve();
  };

  // 表单校验相关
  const commonRule = [
    {
      validator: (rule, value) => {
        if (typeof value === 'string' && value.trim() === '') {
          return Promise.reject(t('form.required'));
        }
        return Promise.resolve();
      },
      message: t('form.required'),
    },
  ];

  const formDescriptor = {
    email: [
      ...commonRule,
      {
        pattern: REGEXP.email,
        message: t('form.email.error'),
      },
    ],
    password: [
      ...commonRule,
      {
        pattern: REGEXP.pwd,
        message: t('form.password.error'),
      },
    ],
    agree: [{ validator: handleCheckAgree }],
  };

  const handleClearError = (name) => {
    setFields([{ name, errors: [] }]);
  };

  const handleEmailFocus = () => {
    handleClearError('email');
    kcsensorsClick(['newEmailInsert', '1']);
  };

  const handlePwdFocus = () => {
    handleClearError('password');
    kcsensorsClick(['newEmailSecret', '1']);
  };

  useFormValidateByLang(form, language, 'email');

  useEffect(() => {
    if (isCount) {
      typeof onFinish === 'function' && onFinish();
    }
  }, [isCount, onFinish]);

  // 曝光事件
  useEffect(() => {
    kcsensorsManualTrack({
      kc_pageid: 'B1register',
      spm: ['newEmailConfirm', '1'],
      ...((trackingConfig && trackingConfig.data) || {}),
    });
  }, []);

  return (
    <ClassNames>
      {({ css }) => {
        const errorClassName = css`
          font-size: 14px !important;
        `;
        const labelClassName = css`
          font-size: 14px;
        `;

        return (
          <Form size="large" form={form}>
            <FormItem
              label={t('email')}
              name="email"
              initialValue={initEmail}
              classNames={{
                error: errorClassName,
                label: labelClassName,
              }}
              rules={formDescriptor.email}
              validateTrigger={['onBlur', 'onSubmit']}
            >
              <AssociativeEmailInput
                namespace={NAMESPACE}
                size="large"
                placeholder={t('email')}
                disabled={false}
                onFocus={handleEmailFocus}
                inputProps={{ autoComplete: 'nope' }}
              />
            </FormItem>
            <FormItem
              label={t('set.login.psw')}
              name="password"
              classNames={{
                error: errorClassName,
                label: labelClassName,
              }}
              initialValue={''}
              rules={formDescriptor.password}
              validateTrigger={['onBlur', 'onSubmit']}
            >
              <NewInputEye size="large" onFocus={handlePwdFocus} />
            </FormItem>
            <FormItem
              label={null}
              name="referralCode"
              initialValue={''}
              classNames={{
                error: errorClassName,
              }}
            >
              <NewInviteCode defaultExpand={false} isNew showDiscount={showDiscount} />
            </FormItem>
            <AgreeFormItem>
              <FormItem
                label={null}
                name="agree"
                classNames={{
                  error: errorClassName,
                }}
                rules={formDescriptor.agree}
                initialValue
                valuePropName={'checked'}
                validateTrigger={['onBlur', 'onSubmit']}
              >
                <Checkbox>
                  <AgreeLabel theme={theme} onClick={(e) => e.stopPropagation()}>
                    {agreeJSX}
                  </AgreeLabel>
                </Checkbox>
              </FormItem>
            </AgreeFormItem>
            <Button fullWidth size="large" loading={loading} onClick={handleSubmit}>
              {t('sign.up')}
              {bonusImg || null}
            </Button>
            <AlreadyHasCount>{forgetLeft}</AlreadyHasCount>
          </Form>
        );
      }}
    </ClassNames>
  );
};

export default EmailSign;
